import "./style.css";
import iconAccident from "../../views/MapView/icons/accident.svg";
import iconHazard from "../../views/MapView/icons/hazard.svg";
import iconJam from "../../views/MapView/icons/jam.svg";
import iconRoadClosed from "../../views/MapView/icons/road_closed.svg";

const ALERT_TYPE_GLYPH = {
  ACCIDENT: iconAccident, HAZARD: iconHazard, JAM: iconJam,
  ROAD_CLOSED: iconRoadClosed,
};

const LEVEL_HEX = {
  5: "#d71307", 4: "#ff7b02", 3: "#ffad03",
  2: "#ffcc01", 1: "#28a446", 0: "#999",
};
const LEVEL_LABEL = {
  5: "Colapso", 4: "Muy lento", 3: "Lento",
  2: "Moderado", 1: "Fluido", 0: "Desconocido",
};
const ALERT_TYPE_LABEL = {
  ACCIDENT: "Accidente", HAZARD: "Peligro", JAM: "Atasco reportado",
  ROAD_CLOSED: "Calle cerrada", POLICE: "Policía", WEATHERHAZARD: "Clima",
};
const SUBTYPE_LABEL = {
  ACCIDENT_MAJOR: "grave", ACCIDENT_MINOR: "menor",
  JAM_HEAVY_TRAFFIC: "tráfico pesado", JAM_MODERATE_TRAFFIC: "tráfico moderado",
  JAM_STAND_STILL_TRAFFIC: "tráfico detenido",
  HAZARD_ON_ROAD: "en la vía", HAZARD_ON_ROAD_CAR_STOPPED: "vehículo detenido",
  HAZARD_ON_ROAD_CONSTRUCTION: "construcción", HAZARD_ON_ROAD_OBJECT: "objeto",
  HAZARD_ON_ROAD_POT_HOLE: "bache",
  HAZARD_ON_SHOULDER_CAR_STOPPED: "vehículo en berma",
  HAZARD_WEATHER: "clima",
  ROAD_CLOSED_HAZARD: "por peligro", ROAD_CLOSED_CONSTRUCTION: "por obra",
  ROAD_CLOSED_EVENT: "por evento",
};
const formatTick = (t) => {
  if (!t) return "—";
  const parts = t.split("-");
  if (parts.length >= 2) return `${parts[0]}:${parts[1]}`;
  return t;
};

/** Estadísticas agregadas sobre un array de features. */
const aggregate = (features) => {
  if (!features?.length) return null;
  // Dedupe por uuid para "atascos únicos".
  const seen = new Set();
  let count = 0;
  let sumLevel = 0;
  let sumSpeed = 0;
  let speedCount = 0;
  let sumLen = 0;
  let lenCount = 0;
  for (const f of features) {
    const p = f.properties || {};
    const u = p.uuid;
    if (u && seen.has(u)) continue;
    if (u) seen.add(u);
    count++;
    sumLevel += p.level || 0;
    if (Number.isFinite(p.speedKMH)) { sumSpeed += p.speedKMH; speedCount++; }
    if (Number.isFinite(p.length)) { sumLen += p.length; lenCount++; }
  }
  return {
    count,
    avgLevel: count ? sumLevel / count : 0,
    avgSpeed: speedCount ? sumSpeed / speedCount : null,
    avgLen: lenCount ? sumLen / lenCount : null,
  };
};


const CustomTooltip = ({ x, y, feature, dateA, dateB, compareFeatures, side = "A" }) => {
  if (!feature) return null;
  const p = feature.properties || {};
  const isAlert = feature.geometry?.type === "Point";
  const street = p.street || p.streetName || "Sin nombre";
  const inCompareMode = Boolean(dateB && !isAlert);

  if (isAlert) return <AlertTooltip x={x} y={y} feature={feature} />;

  if (inCompareMode) {
    // Vista comparativa: stats de la calle en cada fecha.
    const aggB = aggregate(compareFeatures);
    return (
      <div className="tooltip tooltip-compare" style={{ left: x, top: y }}>
        <div className="tooltip-header">
          <span className="tooltip-dot" style={{ background: LEVEL_HEX[p.level || 0] }} />
          <span className="tooltip-street" title={street}>{street}</span>
        </div>
        <div className="tooltip-compare-grid">
          <div className="tooltip-compare-col">
            {/* Color del título por identidad A/B: oro = A, azul = B. En el lado
               B la columna izquierda es B (la feature hovereada). */}
            <div className={`tooltip-compare-date${side === "B" ? " alt" : ""}`}>{dateA}</div>
            <Row label="Nivel" value={(p.level ?? 0).toFixed?.(0) || p.level || 0} />
            <Row label="Velocidad" value={fmtKmh(p.speedKMH)} />
            <Row label="Largo" value={fmtM(p.length)} />
            <Row label="Tick" value={formatTick(p.time)} />
          </div>
          <div className="tooltip-compare-divider" />
          <div className="tooltip-compare-col">
            <div className={`tooltip-compare-date${side === "B" ? "" : " alt"}`}>{dateB}</div>
            {aggB ? (
              <>
                <Row label="Nivel medio" value={aggB.avgLevel.toFixed(1)} />
                <Row label="Veloc. media" value={fmtKmh(aggB.avgSpeed)} />
                <Row label="Largo medio" value={fmtM(aggB.avgLen)} />
                <Row label="Atascos" value={aggB.count} />
              </>
            ) : (
              <div className="tooltip-compare-empty">Sin datos</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Modo normal (sin comparar)
  return (
    <div className="tooltip" style={{ left: x, top: y }}>
      <div className="tooltip-header">
        <span className="tooltip-dot" style={{ background: LEVEL_HEX[p.level || 0] }} />
        <span className="tooltip-street" title={street}>{street}</span>
      </div>
      <div className="tooltip-grid">
        <Cell label="Velocidad" value={fmtKmh(p.speedKMH)} />
        <Cell label="Largo" value={fmtM(p.length)} />
        <Cell label="Nivel" value={
          <>{p.level ?? 0} <span className="tooltip-sub">({LEVEL_LABEL[p.level || 0]})</span></>
        } />
        <Cell label="Hora" value={formatTick(p.time)} />
      </div>
    </div>
  );
};


const AlertTooltip = ({ x, y, feature }) => {
  const p = feature.properties || {};
  const street = p.street || p.streetName || "Sin nombre";
  const typeLbl = ALERT_TYPE_LABEL[p.type] || p.type || "Alerta";
  const subLbl = p.subtype ? SUBTYPE_LABEL[p.subtype] : null;
  const glyph = ALERT_TYPE_GLYPH[p.type];
  return (
    <div className="tooltip tooltip-alert" style={{ left: x, top: y }}>
      <div className="tooltip-header">
        {glyph
          ? <img src={glyph} alt="" className="tooltip-glyph" />
          : <span className="tooltip-dot" style={{ background: "#ff7b02" }} />}
        <div className="tooltip-headtext">
          <div className="tooltip-eyebrow">
            {typeLbl}{subLbl && <span className="tooltip-sub"> · {subLbl}</span>}
          </div>
          <span className="tooltip-street" title={street}>{street}</span>
        </div>
      </div>
      <div className="tooltip-grid">
        <Cell label="Hora del reporte" value={formatTick(p.time)} />
        {Number.isFinite(p.reliability) && (
          <Cell label="Confiabilidad" value={`${p.reliability}/10`} />
        )}
        {Number.isFinite(p.nThumbsUp) && (
          <Cell label="Confirmaciones 👍" value={p.nThumbsUp} />
        )}
        {p.city && <Cell label="Ciudad" value={p.city} />}
      </div>
    </div>
  );
};


const Cell = ({ label, value }) => (
  <div className="tooltip-cell">
    <div className="tooltip-cell-label">{label}</div>
    <div className="tooltip-cell-value">{value}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="tooltip-compare-row">
    <span className="tooltip-compare-label">{label}</span>
    <span className="tooltip-compare-val">{value}</span>
  </div>
);

const fmtKmh = (v) => Number.isFinite(v) ? `${Math.round(v)} km/h` : "—";
const fmtM = (v) => Number.isFinite(v) ? `${Math.round(v)} m` : "—";


export default CustomTooltip;
